import React, { FC, ReactNode } from 'react';
import { Button, Modal, Spin } from 'antd';
import {
	columnLabel,
	ExportColumn,
	ExportField,
	ExportRow,
	spreadsheetColumnName,
	valueAtPath,
} from './ExportColumnsUtils';

interface ExportPreviewModalProps {
	columns: ExportColumn[];
	fields: ExportField[];
	rows: ExportRow[];
	rowCount: number;
	loading: boolean;
	exportButton: ReactNode;
	onClose: () => void;
}

const CELL_CLASS =
	'whitespace-nowrap border-0 border-b border-r border-solid border-grey-5 px-3 py-1';
const GUTTER_CLASS = `${CELL_CLASS} sticky left-0 w-12 bg-grey-3 text-center text-character-secondary`;

const cellText = (row: ExportRow, key: string): string => {
	const value = valueAtPath(row, key);
	return value === null || value === undefined ? '' : `${value}`;
};

const rowCountLabel = (shownCount: number, totalCount: number): string =>
	totalCount !== null && shownCount < totalCount
		? `First ${shownCount} of ${totalCount} rows`
		: `${shownCount} ${shownCount === 1 ? 'row' : 'rows'}`;

const ExportPreviewModal: FC<ExportPreviewModalProps> = ({
	columns,
	fields,
	rows,
	rowCount,
	loading,
	exportButton,
	onClose,
}) => (
	<Modal
		visible={true}
		width="calc(100vw - 32px)"
		style={{ top: 16 }}
		bodyStyle={{ padding: 0 }}
		title={
			<div className="flex items-baseline gap-3">
				<span className="h5medium">Preview</span>
				{!loading ? (
					<span className="bodyregular text-character-secondary">
						{rowCountLabel(rows.length, rowCount)}
					</span>
				) : null}
			</div>
		}
		footer={
			<div className="flex items-center justify-end gap-2">
				<Button onClick={onClose}>Cancel</Button>
				{exportButton}
			</div>
		}
		onCancel={onClose}
	>
		<Spin spinning={loading}>
			<div className="h-[calc(100vh-160px)] overflow-auto">
				<table className="bodyregular border-separate border-spacing-0">
					<thead>
						<tr>
							<th className={`${GUTTER_CLASS} top-0 z-20`} />
							{columns.map((column, index) => (
								<th
									key={column.key}
									className={`${CELL_CLASS} bodyregular sticky top-0 z-10 min-w-[140px] bg-grey-3 text-center text-character-secondary`}
								>
									{spreadsheetColumnName(index)}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						<tr>
							<td className={`${GUTTER_CLASS} z-10`}>1</td>
							{columns.map((column) => (
								<td key={column.key} className={`${CELL_CLASS} bodymedium`}>
									{columnLabel(column, fields)}
								</td>
							))}
						</tr>
						{rows.map((row, rowIndex) => (
							<tr key={rowIndex}>
								<td className={`${GUTTER_CLASS} z-10`}>{rowIndex + 2}</td>
								{columns.map((column) => (
									<td key={column.key} className={CELL_CLASS}>
										{cellText(row, column.key)}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</Spin>
	</Modal>
);

export default ExportPreviewModal;
